import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FileText, CheckCircle, XCircle, Pen, ChevronLeft, ChevronRight, ZoomIn, ZoomOut, ExternalLink } from 'lucide-react';
import { getSigningPage, applySignature, declineSignature } from '../services/esignatureService';
import DOMPurify from 'dompurify';
// react-pdf temporarily disabled for debugging
// import { Document, Page, pdfjs } from 'react-pdf';
// import 'react-pdf/dist/Page/AnnotationLayer.css';
// import 'react-pdf/dist/Page/TextLayer.css';
// pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const PublicSigning = () => {
  const [firmName, setFirmName] = useState('');
  useEffect(() => {
    fetch('/api/public/firm-info')
      .then(r => r.json())
      .then(d => setFirmName(d.firm_name || ''))
      .catch(() => {});
  }, []);

  const { accessToken } = useParams();
  const navigate = useNavigate();
  const canvasRef = useRef(null);
  
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [signing, setSigning] = useState(false);
  const [signingComplete, setSigningComplete] = useState(false);
  const [paymentUrl, setPaymentUrl] = useState(null);
  const [signFirst, setSignFirst] = useState(false);
  const [paymentDueDate, setPaymentDueDate] = useState(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [signatureMode, setSignatureMode] = useState('adopt'); // 'draw', 'type', or 'adopt'
  const [typedSignature, setTypedSignature] = useState('');
  const [pin, setPin] = useState('');
  // A5 UETA: affirmative consent + intent, recorded server-side as events
  const [consentEsign, setConsentEsign] = useState(false);
  const [intentToSign, setIntentToSign] = useState(false);
  const [showDecline, setShowDecline] = useState(false);
  const [declineReason, setDeclineReason] = useState('');
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [pdfScale, setPdfScale] = useState(1.2);
  const [pdfError, setPdfError] = useState(null);

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
    setPageNumber(1);
    setPdfError(null);
  };

  const onDocumentLoadError = (error) => {
    console.error('PDF load error:', error);
    setPdfError(error.message);
  };

  const goToPrevPage = () => setPageNumber(prev => Math.max(prev - 1, 1));
  const goToNextPage = () => setPageNumber(prev => Math.min(prev + 1, numPages || 1));
  const zoomIn = () => setPdfScale(prev => Math.min(prev + 0.2, 2.5));
  const zoomOut = () => setPdfScale(prev => Math.max(prev - 0.2, 0.5));

  useEffect(() => {
    fetchSigningData();
  }, [accessToken]);

  const fetchSigningData = async () => {
    try {
      setLoading(true);
      const response = await getSigningPage(accessToken);
      setData(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching signing data:', err);
      setError(err.response?.data?.detail || 'Invalid or expired signing link');
    } finally {
      setLoading(false);
    }
  };

  const getCanvasCoords = (e, canvas) => {
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    if (e.touches && e.touches.length > 0) {
      return {
        x: (e.touches[0].clientX - rect.left) * scaleX,
        y: (e.touches[0].clientY - rect.top) * scaleY
      };
    }
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY
    };
  };

  const startDrawing = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    if (e.touches) e.preventDefault();
    
    const { x, y } = getCanvasCoords(e, canvas);
    const ctx = canvas.getContext('2d');
    
    setIsDrawing(true);
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    
    const canvas = canvasRef.current;
    if (!canvas) return;
    if (e.touches) e.preventDefault();
    
    const { x, y } = getCanvasCoords(e, canvas);
    const ctx = canvas.getContext('2d');
    
    ctx.lineTo(x, y);
    ctx.strokeStyle = '#1a1a2e';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.stroke();
  };

  const stopDrawing = (e) => {
    if (e && e.touches) e.preventDefault();
    setIsDrawing(false);
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  };

  const generateTypedSignature = () => {
    const canvas = canvasRef.current;
    if (!canvas || !typedSignature) return;
    
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    ctx.font = '48px "Brush Script MT", "Segoe Script", cursive';
    ctx.fillStyle = '#1a1a2e';
    ctx.textBaseline = 'middle';
    ctx.fillText(typedSignature, 20, canvas.height / 2);
  };

  const generateAdoptedSignature = () => {
    const canvas = canvasRef.current;
    if (!canvas || !data?.signer?.name) return;
    
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    const name = data.signer.name;
    ctx.font = '52px "Brush Script MT", "Segoe Script", "Apple Chancery", cursive';
    ctx.fillStyle = '#1a1a2e';
    ctx.textBaseline = 'middle';
    
    // Center the signature
    const metrics = ctx.measureText(name);
    const x = Math.max(20, (canvas.width - metrics.width) / 2);
    ctx.fillText(name, x, canvas.height / 2);
  };

  useEffect(() => {
    if (signatureMode === 'type' && typedSignature) {
      generateTypedSignature();
    }
    if (signatureMode === 'adopt') {
      generateAdoptedSignature();
    }
  }, [typedSignature, signatureMode, data]);

  const handleSign = async () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    // Check if signature is empty
    const ctx = canvas.getContext('2d');
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const pixels = imageData.data;
    let isEmpty = true;
    
    for (let i = 0; i < pixels.length; i += 4) {
      // Check if pixel is not white (signature is dark ink on white bg)
      if (pixels[i] < 250 || pixels[i + 1] < 250 || pixels[i + 2] < 250) {
        isEmpty = false;
        break;
      }
    }
    
    if (isEmpty) {
      alert('Please provide your signature');
      return;
    }
    
    if (data.signer.requires_pin && !pin) {
      alert('Please enter your PIN');
      return;
    }
    
    try {
      setSigning(true);
      const signatureData = canvas.toDataURL('image/png');
      
      if (!consentEsign || !intentToSign) {
        alert('Please confirm both checkboxes to sign electronically.');
        setSigning(false);
        return;
      }
      const result = await applySignature(accessToken, {
        signature_data: signatureData,
        signature_method: signatureMode === 'draw' ? 'drawn' : signatureMode === 'adopt' ? 'adopted' : 'typed',
        typed_name: signatureMode === 'type' ? (typedSignature || null) : null,
        access_pin: pin || null,
        consent_esign: consentEsign,
        intent_to_sign: intentToSign
      });

      // Redirect to payment if available, otherwise show success
      if (result.data?.sign_first) {
        setSignFirst(true);
        setPaymentDueDate(result.data?.payment_due_date || null);
      }
      if (result.data?.payment_url) {
        setSigningComplete(true);
        setPaymentUrl(result.data.payment_url);
      } else {
        setSigningComplete(true);
      }
    } catch (err) {
      console.error('Error applying signature:', err);
      alert(err.response?.data?.detail || 'Failed to apply signature');
    } finally {
      setSigning(false);
    }
  };

  const handleDecline = async () => {
    if (!declineReason.trim()) {
      alert('Please provide a reason for declining');
      return;
    }
    
    if (data.signer.requires_pin && !pin) {
      alert('Please enter your PIN');
      return;
    }
    
    if (!window.confirm('Are you sure you want to decline signing this document?')) {
      return;
    }
    
    try {
      setSigning(true);
      await declineSignature(accessToken, {
        decline_reason: declineReason,
        access_pin: pin || null
      });
      
      alert('You have declined to sign this document.');
    } catch (err) {
      console.error('Error declining signature:', err);
      alert(err.response?.data?.detail || 'Failed to decline signature');
    } finally {
      setSigning(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-virgo-navy-600">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-virgo-teal-400"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-virgo-navy-600">
        <div className="max-w-md w-full p-8 bg-virgo-navy-700 rounded-lg shadow-lg text-center">
          <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-virgo-gold-400 mb-2">Invalid Link</h2>
          <p className="text-virgo-teal-300">{error}</p>
        </div>
      </div>
    );
  }

  if (data.signer.status === 'signed') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-virgo-navy-600">
        <div className="max-w-md w-full p-8 bg-virgo-navy-700 rounded-lg shadow-lg text-center">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-virgo-gold-400 mb-2">Already Signed</h2>
          <p className="text-virgo-teal-300">You have already signed this document.</p>
        </div>
      </div>
    );
  }

  if (signingComplete) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-virgo-navy-600 px-4">
        <div className="max-w-md w-full p-8 bg-virgo-navy-700 rounded-lg shadow-lg text-center">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-virgo-gold-400 mb-2">Document Signed</h2>
          <p className="text-virgo-teal-300 mb-6">Thank you for signing your engagement letter.</p>
          {paymentUrl ? (
            <div>
              <p className="text-virgo-teal-400 text-sm mb-4">
                {signFirst
                  ? (paymentDueDate ? `Your case is now open. Payment of your invoice is due by ${new Date(paymentDueDate.slice(0, 10) + 'T00:00:00').toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' })} — you can pay any time using the button below.` : 'Your case is now open. Your invoice can be paid any time using the button below.')
                  : 'To complete your onboarding, please pay the retainer invoice below.'}
              </p>
              <a
                href={paymentUrl}
                className="inline-block px-8 py-3 bg-gradient-to-r from-cyan-500 to-blue-500 text-white font-semibold rounded-lg hover:from-cyan-600 hover:to-blue-600 transition-all shadow-lg"
              >
                Pay Retainer Invoice
              </a>
            </div>
          ) : (
            <p className="text-virgo-teal-400 text-sm">You will receive an invoice by email shortly.</p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-virgo-navy-600 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="bg-virgo-navy-700 rounded-lg shadow-lg p-6 mb-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
              <FileText className="w-6 h-6 text-virgo-teal-400" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-virgo-gold-400">
                {data.signature_request.document_title}
              </h1>
              <p className="text-sm text-virgo-teal-300 mt-1">
                Please review and sign this document
              </p>
            </div>
          </div>
        </div>

        {/* Document Content */}
        <div className="bg-white rounded-lg shadow-lg overflow-hidden mb-6">
          {data.document_url ? (
            // Display PDF document using PDF.js
            <div className="w-full">
              {/* PDF Toolbar */}
              <div className="p-3 bg-gray-100 border-b flex items-center justify-between flex-wrap gap-2">
                <span className="text-gray-700 font-medium text-sm">
                  {data.signature_request.document_title}
                </span>
                <div className="flex items-center gap-2">
                  {/* Pagination */}
                  {numPages && (
                    <div className="flex items-center gap-1 mr-2">
                      <button
                        onClick={goToPrevPage}
                        disabled={pageNumber <= 1}
                        className="p-1 rounded hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        <ChevronLeft className="w-5 h-5" />
                      </button>
                      <span className="text-sm text-gray-600 min-w-[60px] text-center">
                        {pageNumber} / {numPages}
                      </span>
                      <button
                        onClick={goToNextPage}
                        disabled={pageNumber >= numPages}
                        className="p-1 rounded hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        <ChevronRight className="w-5 h-5" />
                      </button>
                    </div>
                  )}
                  {/* Zoom */}
                  <div className="flex items-center gap-1 border-l pl-2">
                    <button
                      onClick={zoomOut}
                      className="p-1 rounded hover:bg-gray-200"
                      title="Zoom Out"
                    >
                      <ZoomOut className="w-5 h-5" />
                    </button>
                    <span className="text-sm text-gray-600 min-w-[40px] text-center">
                      {Math.round(pdfScale * 100)}%
                    </span>
                    <button
                      onClick={zoomIn}
                      className="p-1 rounded hover:bg-gray-200"
                      title="Zoom In"
                    >
                      <ZoomIn className="w-5 h-5" />
                    </button>
                  </div>
                  {/* Open in new tab */}
                  <a
                    href={data.document_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm ml-2"
                  >
                    <ExternalLink className="w-4 h-4" />
                    Open
                  </a>
                </div>
              </div>

              {/* PDF Document - using iframe fallback */}
              <div className="overflow-auto bg-gray-200" style={{ maxHeight: '650px' }}>
                <iframe
                  src={data.document_url}
                  className="w-full"
                  style={{ height: '600px' }}
                  title="Document to sign"
                />
              </div>

              {/* Error message if PDF failed to load */}
              {pdfError && (
                <div className="p-4 bg-yellow-50 border-t border-yellow-200 text-yellow-800 text-sm">
                  <p className="font-medium">PDF loading issue</p>
                  <p className="mt-1">Please click "Open" above to view the document in a new tab.</p>
                </div>
              )}
            </div>
          ) : data.document_html ? (
            // Render the full HTML engagement letter from template
            <div
              className="p-4"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(data.document_html) }}
            />
          ) : data.document_content ? (
            // Fallback to structured content if no HTML available
            <div className="p-8 text-gray-900 engagement-letter space-y-6">
              {/* Header */}
              <div className="text-center border-b pb-6">
                <h2 className="text-2xl font-bold text-gray-800">ENGAGEMENT LETTER</h2>
                <p className="text-gray-600 mt-2">{firmName || "Our Firm"}</p>
              </div>

              {/* Client Info */}
              <div>
                <p className="mb-4"><strong>Client:</strong> {data.document_content.client_name}</p>
                <p className="mb-4"><strong>Practice Area:</strong> {data.document_content.practice_area}</p>
                {data.document_content.effective_date && (
                  <p className="mb-4"><strong>Effective Date:</strong> {new Date(data.document_content.effective_date).toLocaleDateString()}</p>
                )}
              </div>

              {/* Scope of Representation */}
              <div>
                <h3 className="text-lg font-semibold border-b pb-2 mb-3">Scope of Representation</h3>
                <p className="whitespace-pre-wrap">{data.document_content.scope_of_representation}</p>
              </div>

              {/* Fee Structure */}
              <div>
                <h3 className="text-lg font-semibold border-b pb-2 mb-3">Fee Structure</h3>
                <p className="mb-2"><strong>Fee Type:</strong> {data.document_content.fee_type?.replace('_', ' ')}</p>

                {data.document_content.prepaid_hours && (
                  <p className="mb-2"><strong>Prepaid Hours:</strong> {data.document_content.prepaid_hours} hours @ ${data.document_content.prepaid_rate?.toFixed(2)}/hour</p>
                )}
                {data.document_content.hourly_rate && !data.document_content.prepaid_hours && (
                  <p className="mb-2"><strong>Hourly Rate:</strong> ${data.document_content.hourly_rate.toFixed(2)}/hour</p>
                )}
                {data.document_content.flat_fee_amount && (
                  <p className="mb-2"><strong>Flat Fee:</strong> ${data.document_content.flat_fee_amount.toFixed(2)}</p>
                )}
                {data.document_content.contingency_percentage && (
                  <p className="mb-2"><strong>Contingency:</strong> {data.document_content.contingency_percentage}%</p>
                )}
              </div>

              {/* Signature Section */}
              <div className="border-t pt-6 mt-8">
                <p className="text-sm text-gray-600 mb-4">
                  By signing below, I acknowledge that I have read, understood, and agree to the terms and conditions set forth in this Engagement Letter.
                </p>
              </div>
            </div>
          ) : (
            <div className="p-8 text-center py-8">
              <FileText className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-500">Document content not available</p>
              <p className="text-sm text-gray-400 mt-2">
                Type: {data.signature_request.document_type.replace('_', ' ')}
              </p>
            </div>
          )}
        </div>

        {!showDecline ? (
          <>
            {/* Signing Instructions */}
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4 mb-6">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-amber-500/20 rounded-lg flex-shrink-0">
                  <Pen className="w-5 h-5 text-amber-400" />
                </div>
                <div>
                  <h3 className="text-amber-300 font-semibold mb-1">How to Sign</h3>
                  <ol className="text-amber-200/80 text-sm space-y-1 list-decimal list-inside">
                    <li>Review the document above carefully</li>
                    <li>Adopt the generated signature below, draw your own, or type your name</li>
                    <li>Click "Sign Document" to complete your electronic signature</li>
                  </ol>
                  <p className="text-amber-200/60 text-xs mt-2">
                    Your electronic signature is legally binding and will be applied to the document.
                  </p>
                  <div className="mt-4 space-y-3 text-left">
                    <label className="flex items-start gap-2 text-sm text-slate-300">
                      <input type="checkbox" className="mt-1" checked={consentEsign}
                        onChange={(e) => setConsentEsign(e.target.checked)} />
                      <span>
                        I consent to receive and sign this document electronically. I understand
                        I may request a paper copy, and that I may withdraw this consent for
                        future documents by contacting the firm.
                      </span>
                    </label>
                    <label className="flex items-start gap-2 text-sm text-slate-300">
                      <input type="checkbox" className="mt-1" checked={intentToSign}
                        onChange={(e) => setIntentToSign(e.target.checked)} />
                      <span>
                        I have read and understood this document, and I intend for my electronic
                        signature to bind me to its terms.
                      </span>
                    </label>
                  </div>
                  <p className="hidden">
                  </p>
                </div>
              </div>
            </div>

            {/* Signature Section */}
            <div className="bg-virgo-navy-700 rounded-lg shadow-lg p-6 mb-6">
              <h2 className="text-lg font-semibold text-white mb-4">Your Signature</h2>
              
              {/* Signature Mode Toggle */}
              <div className="flex gap-2 mb-4">
                <button
                  onClick={() => {
                    setSignatureMode('adopt');
                  }}
                  className={`flex-1 px-4 py-2 rounded-lg font-medium ${
                    signatureMode === 'adopt'
                      ? 'bg-virgo-teal-600 text-white'
                      : 'bg-virgo-navy-800 text-virgo-teal-200 hover:bg-virgo-navy-700'
                  }`}
                >
                  Adopt Signature
                </button>
                <button
                  onClick={() => {
                    setSignatureMode('draw');
                    clearSignature();
                  }}
                  className={`flex-1 px-4 py-2 rounded-lg font-medium ${
                    signatureMode === 'draw'
                      ? 'bg-virgo-teal-600 text-white'
                      : 'bg-virgo-navy-800 text-virgo-teal-200 hover:bg-virgo-navy-700'
                  }`}
                >
                  Draw
                </button>
                <button
                  onClick={() => {
                    setSignatureMode('type');
                    clearSignature();
                  }}
                  className={`flex-1 px-4 py-2 rounded-lg font-medium ${
                    signatureMode === 'type'
                      ? 'bg-virgo-teal-600 text-white'
                      : 'bg-virgo-navy-800 text-virgo-teal-200 hover:bg-virgo-navy-700'
                  }`}
                >
                  Type Name
                </button>
              </div>

              {/* Adopt Signature Info */}
              {signatureMode === 'adopt' && (
                <div className="mb-4 p-3 bg-green-900/30 border border-green-700/50 rounded-lg">
                  <p className="text-green-300 text-sm">
                    Your signature has been generated from your name on file. Review it below and click <strong>"Sign Document"</strong> to adopt it as your electronic signature.
                  </p>
                </div>
              )}

              {/* Typed Signature Input */}
              {signatureMode === 'type' && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-virgo-teal-200 mb-2">
                    Type your full name
                  </label>
                  <input
                    type="text"
                    value={typedSignature}
                    onChange={(e) => setTypedSignature(e.target.value)}
                    placeholder="John Doe"
                    className="w-full px-4 py-2 border border-virgo-teal-700 rounded-lg focus:ring-2 focus:ring-virgo-teal-500 focus:border-blue-500"
                  />
                </div>
              )}

              {/* Signature Canvas */}
              <div className="border-2 border-virgo-teal-700 rounded-lg p-2 bg-virgo-navy-700 mb-4">
                <canvas
                  ref={canvasRef}
                  width={700}
                  height={200}
                  onMouseDown={signatureMode === 'draw' ? startDrawing : undefined}
                  onMouseMove={signatureMode === 'draw' ? draw : undefined}
                  onMouseUp={signatureMode === 'draw' ? stopDrawing : undefined}
                  onMouseLeave={signatureMode === 'draw' ? stopDrawing : undefined}
                  onTouchStart={signatureMode === 'draw' ? startDrawing : undefined}
                  onTouchMove={signatureMode === 'draw' ? draw : undefined}
                  onTouchEnd={signatureMode === 'draw' ? stopDrawing : undefined}
                  className="w-full cursor-crosshair rounded"
                  style={{ touchAction: 'none', backgroundColor: '#ffffff' }}
                />
              </div>

              {signatureMode !== 'adopt' && (
                <button
                  onClick={clearSignature}
                  className="text-sm text-virgo-teal-400 hover:text-virgo-teal-300 font-medium"
                >
                  Clear Signature
                </button>
              )}

              {/* PIN Entry */}
              {data.signer.requires_pin && (
                <div className="mt-4">
                  <label className="block text-sm font-medium text-virgo-teal-200 mb-2">
                    PIN (Required)
                  </label>
                  <input
                    type="password"
                    value={pin}
                    onChange={(e) => setPin(e.target.value)}
                    placeholder="Enter your PIN"
                    className="w-full px-4 py-2 border border-virgo-teal-700 rounded-lg focus:ring-2 focus:ring-virgo-teal-500 focus:border-blue-500"
                  />
                </div>
              )}

              {/* Signer Info */}
              <div className="mt-6 p-4 bg-virgo-navy-600 rounded-lg">
                <div className="text-sm text-virgo-teal-300">
                  <p><strong>Name:</strong> {data.signer.name}</p>
                  <p><strong>Email:</strong> {data.signer.email}</p>
                  <p><strong>Type:</strong> {data.signer.type}</p>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="bg-virgo-navy-700 rounded-lg shadow-lg p-6">
              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={handleSign}
                  disabled={signing}
                  className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                >
                  {signing ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      Processing...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-5 h-5" />
                      Sign Document
                    </>
                  )}
                </button>
                
                <button
                  onClick={() => setShowDecline(true)}
                  disabled={signing}
                  className="px-6 py-3 bg-virgo-navy-700 text-red-600 border-2 border-red-600 rounded-lg hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                >
                  Decline to Sign
                </button>
              </div>
            </div>
          </>
        ) : (
          /* Decline Form */
          <div className="bg-virgo-navy-700 rounded-lg shadow-lg p-6">
            <h2 className="text-lg font-semibold text-white mb-4">Decline to Sign</h2>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-virgo-teal-200 mb-2">
                Reason for declining *
              </label>
              <textarea
                value={declineReason}
                onChange={(e) => setDeclineReason(e.target.value)}
                rows={4}
                placeholder="Please explain why you are declining to sign this document..."
                className="w-full px-4 py-2 border border-virgo-teal-700 rounded-lg focus:ring-2 focus:ring-virgo-teal-500 focus:border-blue-500"
                required
              />
            </div>

            {data.signer.requires_pin && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-virgo-teal-200 mb-2">
                  PIN (Required)
                </label>
                <input
                  type="password"
                  value={pin}
                  onChange={(e) => setPin(e.target.value)}
                  placeholder="Enter your PIN"
                  className="w-full px-4 py-2 border border-virgo-teal-700 rounded-lg focus:ring-2 focus:ring-virgo-teal-500 focus:border-blue-500"
                />
              </div>
            )}

            <div className="flex gap-4">
              <button
                onClick={handleDecline}
                disabled={signing}
                className="flex-1 px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
              >
                {signing ? 'Processing...' : 'Confirm Decline'}
              </button>
              
              <button
                onClick={() => {
                  setShowDecline(false);
                  setDeclineReason('');
                }}
                disabled={signing}
                className="px-6 py-3 bg-virgo-navy-700 text-virgo-teal-200 rounded-lg hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PublicSigning;
